<script setup>
import { debounce, isEqual } from "lodash";
import Checkbox from "primevue/checkbox";
import Dropdown from "primevue/dropdown";
import InputText from "primevue/inputtext";
import { computed, defineProps, ref, watch } from "vue";
import AddressComponent from "@/components/forms/AddressInputComponent/AddressComponent.vue";
import WYSIWYG from "@/components/forms/SharedComponents/WYSIWYG/WYSIWYG.vue";
import { convertLatLongDDtoDMS } from "@/components/program-manager/sessions/utils.js";
import { useGetCitiesData } from "@/providers/cities/useGetCitiesData";
import { checkboxPt, dropdownPt } from "./style";

defineOptions({
  name: "MeetingPointInfo",
});

const props = defineProps({
  meetingPointLocation: {
    type: Object,
    required: true,
  },
  disableInputs: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update"]);
const location = ref({ ...props.meetingPointLocation });

watch(
  () => location.value,
  (newVal) => {
    if (!isEqual(props.meetingPointLocation, newVal)) {
      emit("update", { ...newVal });
    }
  },
  { deep: true },
);

watch(
  () => props.meetingPointLocation,
  (newVal) => {
    if (!isEqual(location.value, newVal)) {
      location.value = { ...newVal };
    }
  },
  { deep: true },
);

const initialAddress = {
  street_address_1: location.value?.meeting_point_address_line_1,
  street_address_2: location.value?.meeting_point_address_line_2,
  country: location.value?.meeting_point_city?.country?.name,
  city: location.value?.meeting_point_city?.city_ascii,
  state: location.value?.meeting_point_state,
  postal_code: location.value?.meeting_point_postal_code,
};

const updateAddress = (address) => {
  const { street_address_1, street_address_2, number, state, postal_code, lat, long, address_line_1 } = address;
  location.value.meeting_point_address_line_1 =
    address_line_1 ?? `${number ? `${number} ` : ""}${street_address_1 || ""}`;
  location.value.meeting_point_address_line_2 = street_address_2;
  location.value.meeting_point_state = state;
  location.value.meeting_point_postal_code = postal_code;
  location.value.meeting_point_lat = lat;
  location.value.meeting_point_long = long;
};

const displayLatLong = computed(() => {
  if (!location.value.meeting_point_lat || !location.value.meeting_point_long) {
    return "";
  }

  return convertLatLongDDtoDMS(`${location.value.meeting_point_lat} ${location.value.meeting_point_long}`);
});

const meetingPointCity = ref(location.value?.meeting_point_city);
const citySearchValue = ref("chi");

const {
  data: cityOptions,
  isLoading: citiesIsLoading,
  isPending: citiesIsPending,
} = useGetCitiesData({ limit: 100, page: 1, in_use: false, city_ascii: citySearchValue });

const combinedCityOptions = computed(() => {
  const items = [...(cityOptions.value || [])];

  // if the selected option is not in the current options, include it
  if (meetingPointCity.value && !items.some((city) => city.id === location.meeting_point_city_id)) {
    // if cities are loading, show a loading disabled option
    if (citiesIsLoading.value || citiesIsPending.value) {
      items.push({ id: -1, city_ascii: "Loading...", optionDisabled: true });
    }

    items.push({ ...meetingPointCity.value });
  }

  // add a localFilter property for local filtering, otherwise options will be remove since we allow users to search by City, Country on the endpoint
  return items.map((item) => ({ ...item, localFilter: citySearchValue.value }));
});

const updateCitySearch = debounce(async (event) => {
  const search = event?.value;
  if (search && search?.length > 2) {
    citySearchValue.value = search;
  }
}, 500);

const handleCitySelect = (event) => {
  meetingPointCity.value = cityOptions.value?.find((city) => city.id === event.value) ?? null;
};
</script>

<template>
  <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
    <!-- Meeting Point Name -->
    <div class="flex flex-col gap-2 col-span-2">
      <label for="meeting_point_name" class="common-label-text">Meeting Point Name</label>
      <InputText
        id="meeting_point_name"
        v-model="location.meeting_point_name"
        class="h-10 w-full form-input forced-white-background"
        type="text"
        :disabled="props.disableInputs"
      />
    </div>

    <!-- Meeting Point Address -->
    <div class="col-span-2">
      <label name="meeting_point_address" class="common-label-text">
        Meeting Point Address
      </label>
      <div v-if="props.disableInputs" class="mt-2">
        <p>{{ location.meeting_point_address_line_1 || "" }}</p>
      </div>
      <AddressComponent
        v-else
        class="forced-white-background"
        :is-required="false"
        :initial-address-info="initialAddress"
        @update-address-info="updateAddress"
      />
    </div>
    <div class="flex flex-col gap-2">
      <label for="meeting_point_address_line_1" class="common-label-text">Address Line 1</label>
      <InputText
        id="meeting_point_address_line_1"
        v-model="location.meeting_point_address_line_1"
        class="h-10 w-full form-input forced-white-background"
        type="text"
        :disabled="props.disableInputs"
      />
    </div>
    <div class="flex flex-col gap-2">
      <label for="meeting_point_city" class="common-label-text">City</label>
      <div class="relative w-full">
        <Dropdown
          v-model="location.meeting_point_city_id"
          option-label="city_ascii"
          option-value="id"
          option-disabled="optionDisabled"
          :options="combinedCityOptions"
          :filter="true"
          :loading="citiesIsLoading"
          :show-clear="true"
          :disabled="props.disableInputs"
          :filter-fields="['localFilter']"
          :pt="dropdownPt"
          @filter="updateCitySearch"
          @change="handleCitySelect"
        >
            <template #option="{option}">
              <div class="text-sm">
                <div class="font-semibold">{{ option.city_ascii }}</div>
                <div>
                  {{ option.country?.name }}
                  <span v-if="option.country?.name && option.admin_name_ascii">-</span>
                  {{ option.admin_name_ascii }}
                </div>
              </div>
            </template>
            <template #emptyfilter>
              <div v-if="citiesIsLoading || citiesIsPending">Loading ...</div>
              <div v-else>No Results Found</div>
            </template>
          </Dropdown>
      </div>
    </div>
    <div>
      <label name="country" class="common-label-text">
        Country
      </label>
      <div class="my-2 h-6">
        <p>{{ meetingPointCity?.country?.name || "" }}</p>
      </div>
    </div>
    <div>
      <label name="latLong" class="common-label-text">
        Zip Code
      </label>
      <div class="my-2 h-6">
        <p>{{ location.meeting_point_postal_code || "" }}</p>
      </div>
    </div>
    <div>
      <label name="latLong" class="common-label-text">
        Lat/Long
      </label>
      <div class="my-2 h-6">
        <p>{{ displayLatLong }}</p>
      </div>
    </div>
    <div class="flex gap-2">
      <Checkbox id="meeting_point_final" v-model="location.meeting_point_final" binary :pt="checkboxPt"/>
      <label for="meeting_point_final" class="common-label-text">Meeting Point is final</label>
    </div>

    <!-- Meeting Point Instructions -->
    <div class="col-span-2">
      <label class="common-label-text">
        Meeting Point Instructions
      </label>
      <WYSIWYG v-model="location.meeting_point_instructions" :editable="!props.disableInputs" :character-limit="500" />
    </div>
  </div>
</template>
